// ─── resolveActivePlanPublication.js ────────────────────────────────────
// Refonte écran Suivi — statut de publication du PLAN ACTIF (version minimale).
//
// Fonction PURE : dérive, à partir de l'entrée clients-status déjà fetchée
// (fetchClientsStatus), un descripteur compact « où en est la publication du
// plan actif » pour l'afficher sur la frise (chip d'en-tête).
//
// Portée assumée : le plan ACTIF uniquement (pas d'historique par version —
// le numéro de version publiée n'est pas exposé côté SaaS, cf.
// buildPublicationMarkers). C'est la « version minimale » validée : effet
// visible tout de suite, zéro migration.
//
// FAIL-CLOSED (aligné sur les défauts durcis de fetchClientsStatus V97.35) :
// sans donnée fiable, on renvoie `unknown` — JAMAIS « visible » à tort.

export const PUB_STATE = {
  VISIBLE: 'visible',           // le plan est visible par la cliente maintenant
  SCHEDULED: 'scheduled',       // publié, deviendra visible à une date future
  PUBLISHED_PENDING: 'published_pending', // publié mais pas (encore) visible, sans date claire
  UNKNOWN: 'unknown',           // pas de certitude → on n'affiche rien d'affirmatif
};

/**
 * @param {object|null} statusEntry - entrée clients-status (fetchClientsStatus)
 * @param {Date} [now]
 * @returns {{ state:string, label:string, date:string|null, tone:'ok'|'scheduled'|'muted', reason:string|null }}
 */
export function resolveActivePlanPublication(statusEntry, now = new Date()) {
  const UNKNOWN = { state: PUB_STATE.UNKNOWN, label: 'Statut de publication inconnu', date: null, tone: 'muted', reason: null };

  // Pas de donnée fiable → inconnu (config app absente, réseau KO, cliente non
  // trouvée, compte non activé). On n'affirme jamais « visible » sans preuve.
  if (!statusEntry || statusEntry.found === false || statusEntry.account_enabled === false) {
    return UNKNOWN;
  }
  if (statusEntry.account_deleted === true) {
    return { ...UNKNOWN, label: 'Compte cliente supprimé' };
  }

  // Visible maintenant : le signal le plus fort et le plus utile pour Anissa.
  if (statusEntry.visible_now === true) {
    return { state: PUB_STATE.VISIBLE, label: 'Visible par la cliente', date: statusEntry.published_at || null, tone: 'ok', reason: null };
  }

  // Programmée : effective_at dans le futur (gating temporel des suivis).
  const eff = statusEntry.effective_at ? Date.parse(statusEntry.effective_at) : NaN;
  if (!Number.isNaN(eff) && eff > now.getTime()) {
    return { state: PUB_STATE.SCHEDULED, label: 'Programmée', date: statusEntry.effective_at, tone: 'scheduled', reason: null };
  }

  // Publiée mais pas visible, sans date claire (raison côté app cliente).
  if (statusEntry.published_at || statusEntry.plan_status) {
    return {
      state: PUB_STATE.PUBLISHED_PENDING,
      label: 'Publiée, pas encore visible',
      date: statusEntry.published_at || null,
      tone: 'muted',
      reason: statusEntry.reason_if_not_visible || null,
    };
  }

  return UNKNOWN;
}
